import { IGift, IGiftActions } from '../../model';

import { CartPageReducer, ECartActions, ICartPageState } from './model/reducer';
import { isProceedCheckoutEnabled } from './utils';

export const reducerInitialState: ICartPageState = {
  benefits: { loyalties: [], vouchers: [] },
  serviceItems: {
    giftPackagings: [],
    insurance: null,
    ecoPackaging: null,
    fundRaising: null,
    discoveryBox: [],
  },
  products: [],
  discounts: [],
  gifts: [],
  pricesSummary: {
    priceDelivery: 0,
    priceItemSummary: 0,
    pricePayment: 0,
    priceProductsSummary: 0,
    discountWithoutDelivery: 0,
    vat: 0,
  },
  proceedCheckoutEnabled: false,
  copiedProductVoucherCode: '',
  loyaltyRewardRemoved: false,
};

const updateGiftItem = (
  gift: IGift,
  giftsChoose: number,
  selectedCount: number,
  giftProductId: number,
  giftItemId: number | undefined,
): IGift => {
  //make copy of the current gift
  const giftTemp = { ...gift };

  //if action is single select -- first we remove previously selected
  if (giftsChoose <= 1) {
    giftTemp.selected = false;
    giftTemp.itemId = 0;
  }

  //select desired item if the maximum select count was not exceeded - for multiple select actions
  //or
  //select desired item if it is single select - for single select actions
  //or
  //user is deselecting already selected item
  const canSelectItem =
    giftTemp.productId === giftProductId && (giftsChoose <= 1 || selectedCount < giftsChoose || gift.selected);

  if (canSelectItem) {
    giftTemp.itemId = giftTemp.selected || !giftItemId ? 0 : giftItemId;
    giftTemp.selected = !giftTemp.selected;
  }

  return giftTemp;
};

// Function for update gifts list
const updateGiftList = (
  giftsList: IGiftActions[],
  giftProductId: number,
  giftListId: number,
  giftItemId: number | undefined,
): IGiftActions[] =>
  giftsList.map((gifts) => {
    // check if gift action id equals to id of requested action
    if (gifts.id !== giftListId) return gifts;

    //get currently selected items count
    const selectedCount = gifts.assignGiftList.filter((o) => o.selected).length;

    const giftList = gifts.assignGiftList.map((gift) =>
      updateGiftItem(gift, gifts.choose, selectedCount, giftProductId, giftItemId),
    );

    return { ...gifts, assignGiftList: giftList };
  });

export const cartPageReducer: CartPageReducer = (prevState, action) => {
  switch (action.actionType) {
    case ECartActions.UPDATE_COMPLETE_STATE: {
      return {
        ...action.payload,
        proceedCheckoutEnabled: isProceedCheckoutEnabled(
          action.payload.products,
          action.payload.serviceItems.discoveryBox,
        ),
        copiedProductVoucherCode: prevState.copiedProductVoucherCode,
        loyaltyRewardRemoved: prevState.loyaltyRewardRemoved,
      };
    }
    case ECartActions.UPDATE_PRODUCTS: {
      const {
        pricesSummary,
        gifts,
        products,
        discounts,
        benefits,
        serviceItems: { giftPackagings, ecoPackaging },
      } = action.payload;
      return {
        ...prevState,
        products,
        discounts,
        gifts,
        pricesSummary,
        benefits,
        serviceItems: {
          ...prevState.serviceItems,
          giftPackagings: giftPackagings,
          ecoPackaging: ecoPackaging,
        },
        proceedCheckoutEnabled: isProceedCheckoutEnabled(products, prevState.serviceItems.discoveryBox),
        copiedProductVoucherCode: prevState.copiedProductVoucherCode,
        loyaltyRewardRemoved: prevState.loyaltyRewardRemoved,
      };
    }
    case ECartActions.TOGGLE_GIFT_PACKAGING: {
      const { giftPackagings, pricesSummary } = action.payload;
      return {
        ...prevState,
        serviceItems: { ...prevState.serviceItems, giftPackagings },
        pricesSummary,
      };
    }
    case ECartActions.TOGGLE_ECO_PACKAGING: {
      const { ecoPackaging, pricesSummary } = action.payload;
      return {
        ...prevState,
        serviceItems: { ...prevState.serviceItems, ecoPackaging },
        pricesSummary,
      };
    }
    case ECartActions.TOGGLE_INSURANCE: {
      const { insurance, pricesSummary } = action.payload;
      return {
        ...prevState,
        serviceItems: { ...prevState.serviceItems, insurance },
        pricesSummary,
      };
    }
    case ECartActions.UPDATE_GIFT_SELECTION: {
      // Get payload properties
      const { giftProductId, giftListId, giftItemId } = action.payload;

      const updatedGifts = updateGiftList(prevState.gifts, giftProductId, giftListId, giftItemId);

      return {
        ...prevState,
        gifts: updatedGifts,
      };
    }
    case ECartActions.UPDATE_VOUCHER: {
      const { products, benefits, pricesSummary } = action.payload;
      return {
        ...prevState,
        products: typeof products === 'undefined' ? prevState.products : products,
        benefits,
        pricesSummary,
      };
    }
    case ECartActions.UPDATE_NON_STANDARD_PRODUCTS: {
      const {
        products,
        pricesSummary,
        discounts,
        serviceItems: { giftPackagings },
        benefits,
      } = action.payload;
      return {
        ...prevState,
        products,
        pricesSummary,
        discounts,
        benefits,
        serviceItems: {
          ...prevState.serviceItems,
          giftPackagings: giftPackagings,
        },
        proceedCheckoutEnabled: isProceedCheckoutEnabled(products, prevState.serviceItems.discoveryBox),
        copiedProductVoucherCode: prevState.copiedProductVoucherCode,
      };
    }
    case ECartActions.TOGGLE_FUND_RAISING: {
      const { fundRaising, pricesSummary } = action.payload;
      return {
        ...prevState,
        serviceItems: {
          ...prevState.serviceItems,
          fundRaising,
        },
        pricesSummary,
      };
    }
    case ECartActions.REMOVE_DISCOVERY_BOX: {
      const { products, discoveryBox, pricesSummary } = action.payload;
      return {
        ...prevState,
        products,
        serviceItems: {
          ...prevState.serviceItems,
          discoveryBox,
        },
        pricesSummary,
        proceedCheckoutEnabled: isProceedCheckoutEnabled(products, discoveryBox),
      };
    }
    case ECartActions.UPDATE_BENEFITS: {
      return { ...prevState, ...action.payload };
    }
    case ECartActions.SET_COPIED_PRODUCT_VOUCHER_CODE:
      return { ...prevState, copiedProductVoucherCode: action.payload.productVoucherCode };
    case ECartActions.SET_REMOVED_LOYALTY_REWARD:
      return { ...prevState, loyaltyRewardRemoved: action.payload.loyaltyRewardRemoved };
    default:
      return prevState;
  }
};
